import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

/**
 * CountdownBadge
 *
 * Displays the remaining time of a temporary promotion next to a price.
 * ``endsAt`` is a calendar date in ``YYYY-MM-DD`` format (the schema
 * currently used by promotions). We interpret it as end-of-day local time
 * (``T23:59:59``), matching the backend's inclusive-date logic.
 *
 * Format cascades based on remaining time:
 *   - > 1 day   → "2d 14h 32m"          (informative)
 *   - < 1 day   → "14h 32m 07s"         (attention)
 *   - < 1 hour  → "32m 07s" + red pulse (urgent)
 *
 * When the countdown reaches 0 the component renders ``null`` and invokes
 * ``onExpire`` exactly once so the parent can drop the promotion from its
 * state.
 */

const pad = (n) => String(n).padStart(2, "0");

function computeRemaining(endsAt) {
  if (!endsAt) return null;
  const deadline = new Date(`${endsAt}T23:59:59`).getTime();
  if (Number.isNaN(deadline)) return null;
  const diff = deadline - Date.now();
  if (diff <= 0) return null;
  const s = Math.floor(diff / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
    totalMs: diff,
  };
}

function formatRemaining(r) {
  const { days, hours, minutes, seconds, totalMs } = r;
  if (days > 0) return `${days}d ${pad(hours)}h ${pad(minutes)}m`;
  if (totalMs >= 60 * 60 * 1000) return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  return `${pad(minutes)}m ${pad(seconds)}s`;
}

export default function CountdownBadge({ endsAt, onExpire, className = "", testId }) {
  const [remaining, setRemaining] = useState(() => computeRemaining(endsAt));

  useEffect(() => {
    if (!endsAt) return undefined;
    const initial = computeRemaining(endsAt);
    setRemaining(initial);
    if (!initial) {
      onExpire?.();
      return undefined;
    }
    const id = setInterval(() => {
      const r = computeRemaining(endsAt);
      setRemaining(r);
      if (!r) {
        clearInterval(id);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [endsAt, onExpire]);

  if (!remaining) return null;

  const urgent = remaining.totalMs < 60 * 60 * 1000;
  const soon = !urgent && remaining.totalMs < 24 * 60 * 60 * 1000;
  const display = formatRemaining(remaining);

  const styles = urgent
    ? "bg-gradient-to-r from-rose-500 to-red-500 text-white border-transparent shadow-md shadow-rose-500/40 animate-pulse"
    : soon
    ? "bg-gradient-to-r from-amber-400 to-orange-400 text-white border-transparent shadow-sm shadow-amber-400/30"
    : "bg-amber-50 text-amber-800 border-amber-200";

  return (
    <span
      className={
        "inline-flex items-center gap-1.5 text-xs font-mono-data tabular-nums font-semibold px-2.5 py-1 rounded-full border transition-all " +
        styles +
        " " +
        className
      }
      title={`La oferta termina en ${display}`}
      aria-label={`La oferta termina en ${display}`}
      data-testid={testId}
    >
      <Clock className="w-3.5 h-3.5" aria-hidden="true" />
      {display}
    </span>
  );
}
